import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useBranch } from "@/contexts/BranchContext";
import { useAuth } from "@/contexts/AuthContext";
import StatusBadge from "@/components/StatusBadge";
import { Badge } from "@/components/ui/badge";
import { fmtDateTime, fmtTime } from "@/lib/format";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { CalendarClock, Phone, Plus, Pencil, Receipt, Trash2, TrendingUp, Building2, MessageCircle, Search, Download } from "lucide-react";
import BookingFormDialog from "@/components/BookingFormDialog";
import TransactionFormDialog from "@/components/TransactionFormDialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";

const BRANCH_COLORS = [
  "hsl(173 58% 39%)",
  "hsl(271 76% 53%)",
  "hsl(24 95% 53%)",
  "hsl(197 71% 52%)",
  "hsl(142 71% 45%)",
  "hsl(340 75% 55%)",
  "hsl(43 89% 50%)",
];

const shortBranchName = (name) => (name || "").replace(/^MyClinic\s+/i, "");

const STATUSES = ["scheduled", "arrived", "treatment", "finished", "reschedule", "cancel"];
const CAT_LABEL = { new_customer: "New", repeat_customer: "Repeat" };
const SOURCE_LABEL = {
  whatsapp: "WhatsApp", instagram: "Instagram", tiktok: "TikTok",
  ads: "Ads (Meta/Google)", ads_ots: "Ads OTS", ads_cs_cabang: "Ads CS Cabang", walk_in: "Walk-in", referral: "Referral", other: "Lainnya",
};

export default function Bookings() {
  const { branchId, branches } = useBranch();
  const { user } = useAuth();
  const isCsCrm = ["cs", "crm"].includes(user?.role);
  const canEdit = ["owner", "super_admin", "cs", "crm"].includes(user?.role);
  const canDelete = ["owner", "super_admin"].includes(user?.role);
  const todayLocal = new Date();
  const isoDate = (d) => d.toISOString().slice(0, 10);
  const [dateFrom, setDateFrom] = useState(isoDate(new Date(todayLocal.getTime() - 6 * 86400000)));
  const [dateTo, setDateTo] = useState(isoDate(new Date(todayLocal.getTime() + 7 * 86400000)));
  const [tab, setTab] = useState("all");
  const [search, setSearch] = useState("");
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [txOpen, setTxOpen] = useState(false);
  const [txBooking, setTxBooking] = useState(null);

  const treatmentLabel = (b) =>
    b.treatment_name || (b.consultation_type === "consultation" ? "Konsultasi Dokter" : "—");

  const fetchBookings = () => {
    setLoading(true);
    api
      .get("/bookings", {
        params: { branch_id: branchId, date_from: dateFrom, date_to: dateTo + "T23:59:59" },
      })
      .then((r) => setBookings(r.data))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchBookings();
  }, [branchId, dateFrom, dateTo]);

  const downloadExcel = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/reports/appointments-export", {
        params: { branch_id: branchId, date_from: dateFrom, date_to: dateTo + "T23:59:59" },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Daftar_Appointment_${dateFrom}_${dateTo}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Excel appointment diunduh");
    } catch {
      toast.error("Gagal mengunduh Excel");
    } finally {
      setDownloading(false);
    }
  };

  const filtered = useMemo(() => {
    const byTab = tab === "all" ? bookings : bookings.filter((b) => b.status === tab);
    const q = search.trim().toLowerCase();
    if (!q) return byTab;
    return byTab.filter((b) =>
      (b.patient_name || "").toLowerCase().includes(q) ||
      (b.treatment_name || "").toLowerCase().includes(q) ||
      (b.patient_phone || "").toLowerCase().includes(q) ||
      (b.booking_at || "").slice(0, 10).includes(q)
    );
  }, [bookings, tab, search]);

  const counts = useMemo(() => {
    const c = { all: bookings.length };
    for (const s of STATUSES) c[s] = bookings.filter((b) => b.status === s).length;
    return c;
  }, [bookings]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/bookings/${id}/status`, { status });
      toast.success(`Status diubah ke ${status}`);
      fetchBookings();
    } catch (e) {
      toast.error("Gagal mengubah status");
    }
  };

  const ATTENDANCE_OPTS = [
    ["show_up", "Closing / Show Up"],
    ["no_show_reschedule", "No Show - Reschedule"],
    ["no_show_cancel", "No Show - Cancel"],
    ["pending", "Reset (Belum ditandai)"],
  ];
  const canAttendance = ["owner", "super_admin", "manager", "fo_cs", "cs", "crm"].includes(user?.role);
  const updateAttendance = async (id, attendance_status) => {
    try {
      await api.patch(`/bookings/${id}/attendance`, { attendance_status });
      toast.success("Status kehadiran diperbarui");
      fetchBookings();
    } catch (e) {
      toast.error("Gagal update kehadiran");
    }
  };
  const ATT_LABEL = { show_up: "Show Up", no_show_reschedule: "No Show-Resch", no_show_cancel: "No Show-Cancel", pending: "" };

  const openCreate = () => {
    setEditing(null);
    setDialogOpen(true);
  };

  const openEdit = (b) => {
    setEditing(b);
    setDialogOpen(true);
  };

  const openTx = (b) => {
    setTxBooking(b);
    setTxOpen(true);
  };

  const deleteBooking = async (id) => {
    if (!window.confirm("Hapus booking ini?")) return;
    try {
      await api.delete(`/bookings/${id}`);
      toast.success("Booking dihapus");
      fetchBookings();
    } catch {
      toast.error("Gagal menghapus");
    }
  };

  const sendWaFollowup = async (bookingId, template = "auto") => {
    try {
      const { data } = await api.get(`/bookings/${bookingId}/wa-followup`, { params: { template } });
      if (!data.wa_url) {
        toast.error("Customer tidak memiliki no WhatsApp");
        return;
      }
      window.open(data.wa_url, "_blank", "noopener,noreferrer");
      toast.success(`Template "${data.label}" terbuka di WhatsApp`);
    } catch (err) {
      toast.error("Gagal generate template WA");
    }
  };

  // Group bookings by day for the visual slot view
  const grouped = useMemo(() => {
    const map = {};
    for (const b of filtered) {
      const day = (b.booking_at || "").slice(0, 10);
      if (!map[day]) map[day] = [];
      map[day].push(b);
    }
    return Object.entries(map).sort(([a], [b]) => a.localeCompare(b));
  }, [filtered]);

  // Branch color & name map
  const branchMap = useMemo(() => {
    const m = {};
    (branches || []).forEach((b, i) => {
      m[b.id] = { name: b.name, short: shortBranchName(b.name), color: BRANCH_COLORS[i % BRANCH_COLORS.length] };
    });
    return m;
  }, [branches]);

  // Daily booking trend (per-branch stacked), built from filtered bookings
  const trendData = useMemo(() => {
    const byDay = {};
    for (const b of filtered) {
      const day = (b.booking_at || "").slice(0, 10);
      if (!day) continue;
      if (!byDay[day]) {
        byDay[day] = { date: day, total: 0 };
        (branches || []).forEach((br) => {
          byDay[day][br.id] = 0;
        });
      }
      byDay[day][b.branch_id] = (byDay[day][b.branch_id] || 0) + 1;
      byDay[day].total += 1;
    }
    return Object.values(byDay).sort((a, b) => a.date.localeCompare(b.date));
  }, [filtered, branches]);

  const visibleBranchIds = useMemo(() => {
    if (branchId && branchId !== "all") return [branchId];
    return (branches || []).map((b) => b.id);
  }, [branchId, branches]);

  return (
    <div className="space-y-6" data-testid="bookings-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl tracking-tight">Booking Dashboard</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Pantau slot booking realtime, lengkap dengan status warna eye-catching.
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <label className="text-xs text-muted-foreground">Dari</label>
            <Input
              type="date"
              data-testid="bookings-date-from"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="w-[160px]"
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Sampai</label>
            <Input
              type="date"
              data-testid="bookings-date-to"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="w-[160px]"
            />
          </div>
          <Button variant="outline" data-testid="bookings-download-btn" onClick={downloadExcel} disabled={downloading} className="h-11 md:h-10">
            <Download className="mr-1.5 h-4 w-4" /> {downloading ? "Mengunduh..." : "Download Excel"}
          </Button>
          <Button data-testid="add-booking-btn" onClick={openCreate} className="h-11 md:h-10 flex-1 md:flex-none">
            <Plus className="mr-1.5 h-4 w-4" /> Tambah Booking
          </Button>
        </div>
      </div>

      {/* Search (patient name, date, treatment) */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          data-testid="bookings-search"
          placeholder="Cari nama pasien, no HP, treatment, atau tanggal (YYYY-MM-DD)..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9 h-12 text-base md:h-10 md:text-sm"
        />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="bookings-tabs">
          <TabsTrigger value="all" data-testid="tab-all">
            Semua <span className="ml-1.5 text-muted-foreground">({counts.all})</span>
          </TabsTrigger>
          {STATUSES.map((s) => (
            <TabsTrigger key={s} value={s} data-testid={`tab-${s}`}>
              <span className="capitalize">{s}</span>
              <span className="ml-1.5 text-muted-foreground">({counts[s]})</span>
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {/* Booking trend chart */}
      <Card data-testid="booking-trend-card">
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <TrendingUp className="h-4 w-4" /> Trend Visitor / Booking
          </CardTitle>
          <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
            <span>
              {branchId === "all"
                ? "Stacked per cabang. Pilih cabang di header untuk fokus 1 cabang."
                : `Filter aktif: ${branchMap[branchId]?.name || branchId}`}
            </span>
            <span className="inline-flex items-center gap-3 ml-auto">
              {visibleBranchIds.map((bid) => (
                <span key={bid} className="inline-flex items-center gap-1.5" data-testid={`legend-${bid}`}>
                  <span
                    className="inline-block h-2.5 w-2.5 rounded-sm"
                    style={{ backgroundColor: branchMap[bid]?.color }}
                  />
                  {branchMap[bid]?.short || "Cabang"}
                </span>
              ))}
            </span>
          </div>
        </CardHeader>
        <CardContent>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={trendData} margin={{ left: -16, right: 8, top: 8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                <XAxis dataKey="date" tickFormatter={(v) => v.slice(5)} tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip
                  formatter={(v, name) => [`${v} booking`, name]}
                  labelFormatter={(l) => `Tanggal ${l}`}
                />
                {visibleBranchIds.map((bid) => (
                  <Bar
                    key={bid}
                    dataKey={bid}
                    name={branchMap[bid]?.short || "Cabang"}
                    stackId="a"
                    fill={branchMap[bid]?.color || "#888"}
                    radius={[4, 4, 0, 0]}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
          {trendData.length === 0 && (
            <div className="py-6 text-center text-sm text-muted-foreground">
              Belum ada booking di rentang tanggal yang dipilih.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Visual slot dashboard (per-day grouped) */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <CalendarClock className="h-4 w-4" /> Visual Slot Per Hari
          </CardTitle>
        </CardHeader>
        <CardContent>
          {grouped.length === 0 && (
            <div className="text-sm text-muted-foreground">
              {loading ? "Memuat..." : "Tidak ada booking di rentang ini."}
            </div>
          )}
          <div className="space-y-4">
            {grouped.map(([day, items]) => (
              <div key={day} data-testid={`day-group-${day}`}>
                <div className="mb-2 text-xs uppercase tracking-widest text-muted-foreground">
                  {new Date(day).toLocaleDateString("id-ID", {
                    weekday: "long",
                    day: "numeric",
                    month: "long",
                  })}{" "}
                  • {items.length} slot
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                  {items
                    .sort((a, b) => a.booking_at.localeCompare(b.booking_at))
                    .map((b) => {
                      const br = branchMap[b.branch_id];
                      return (
                      <div
                        key={b.id}
                        data-testid={`slot-${b.id}`}
                        className="rounded-md border border-border bg-card p-3 hover:shadow-sm transition-all"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-heading text-sm">{fmtTime(b.booking_at)}</span>
                          <div className="flex items-center gap-1.5">
                            <StatusBadge status={b.status} />
                            <DropdownMenu>
                              <DropdownMenuTrigger
                                data-testid={`wa-${b.id}`}
                                className="grid h-6 w-6 place-items-center rounded-full bg-emerald-100 text-emerald-700 hover:bg-emerald-200 transition-colors"
                                title="Kirim WhatsApp template"
                              >
                                <MessageCircle className="h-3.5 w-3.5" />
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="min-w-[200px]">
                                <DropdownMenuLabel className="text-xs">WhatsApp Template</DropdownMenuLabel>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem data-testid={`wa-${b.id}-auto`} onClick={() => sendWaFollowup(b.id, "auto")}>
                                  <span className="text-xs">🤖 Auto (smart pick)</span>
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => sendWaFollowup(b.id, "h3")}><span className="text-xs">📅 Reminder H-3</span></DropdownMenuItem>
                                <DropdownMenuItem onClick={() => sendWaFollowup(b.id, "h2")}><span className="text-xs">📆 Reminder H-2</span></DropdownMenuItem>
                                <DropdownMenuItem onClick={() => sendWaFollowup(b.id, "h1")}><span className="text-xs">⏰ Reminder H-1</span></DropdownMenuItem>
                                <DropdownMenuItem onClick={() => sendWaFollowup(b.id, "h0")}><span className="text-xs">🌟 Welcome Hari H</span></DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem data-testid={`wa-${b.id}-noshow`} onClick={() => sendWaFollowup(b.id, "no_show")}><span className="text-xs">💝 Re-engage No-Show</span></DropdownMenuItem>
                                <DropdownMenuItem data-testid={`wa-${b.id}-after`} onClick={() => sendWaFollowup(b.id, "after_treatment")}><span className="text-xs">🌷 Aftercare & Edukasi</span></DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </div>
                        <div className="mt-1.5 truncate text-sm font-medium">
                          {b.patient_name}
                        </div>
                        <div className="truncate text-xs text-muted-foreground">
                          {treatmentLabel(b)} • {b.doctor_name || "—"}
                        </div>
                        {b.consultation_type === "consultation" && (
                          <span
                            data-testid={`slot-consult-${b.id}`}
                            className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-medium text-indigo-700 border border-indigo-200"
                          >
                            Konsultasi Dokter
                          </span>
                        )}
                        {br && (
                          <div
                            className="mt-2 inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10px] font-medium"
                            data-testid={`slot-branch-${b.id}`}
                            style={{
                              backgroundColor: `${br.color}1f`,
                              color: br.color,
                              borderLeft: `3px solid ${br.color}`,
                            }}
                          >
                            <Building2 className="h-3 w-3" />
                            {br.short}
                          </div>
                        )}
                        <div className="mt-2 flex flex-wrap items-center gap-1">
                          {STATUSES.filter((s) => s !== b.status).map((s) => (
                            <button
                              key={s}
                              data-testid={`set-${b.id}-${s}`}
                              onClick={() => updateStatus(b.id, s)}
                              className="rounded border border-border bg-background px-1.5 py-0.5 text-[10px] text-muted-foreground hover:bg-secondary capitalize"
                            >
                              {s}
                            </button>
                          ))}
                        </div>
                      </div>
                      );
                    })}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Tabular full list */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-lg">Daftar Booking</CardTitle>
        </CardHeader>
        <CardContent>
          {/* Mobile: card layout (no horizontal scroll) */}
          <div className="md:hidden space-y-3">
            {filtered.slice(0, 200).map((b) => (
              <div key={b.id} data-testid={`booking-card-${b.id}`} className="rounded-lg border border-border bg-card p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-medium text-sm truncate">{b.patient_name}</div>
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <Phone className="h-3 w-3" /> {b.patient_phone || "—"}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <StatusBadge status={b.status} />
                    {b.attendance_status && b.attendance_status !== "pending" && (
                      <span className="mt-1 block text-[10px] font-medium text-emerald-700">{ATT_LABEL[b.attendance_status]}</span>
                    )}
                  </div>
                </div>
                <div className="mt-2 text-xs text-muted-foreground">{fmtDateTime(b.booking_at)}</div>
                <div className="mt-0.5 text-sm">{treatmentLabel(b)} • {b.doctor_name || "—"}</div>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  {canEdit && (
                    <Button size="sm" variant="outline" className="h-10 flex-1" data-testid={`edit-booking-m-${b.id}`} onClick={() => openEdit(b)}>
                      <Pencil className="mr-1.5 h-4 w-4" /> Edit
                    </Button>
                  )}
                  {canAttendance && (
                    <Select onValueChange={(v) => updateAttendance(b.id, v)} value={undefined}>
                      <SelectTrigger data-testid={`attendance-select-m-${b.id}`} className="h-10 flex-1 min-w-[130px]">
                        <SelectValue placeholder="Kehadiran" />
                      </SelectTrigger>
                      <SelectContent>
                        {ATTENDANCE_OPTS.map(([v, l]) => (<SelectItem key={v} value={v}>{l}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  )}
                  {b.status !== "finished" && b.status !== "cancel" && (
                    <Button size="sm" variant="outline" className="h-10" data-testid={`tx-booking-m-${b.id}`} onClick={() => openTx(b)} title="Catat transaksi">
                      <Receipt className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
            {filtered.length === 0 && !loading && (
              <div className="text-center text-sm text-muted-foreground py-10">Tidak ada booking.</div>
            )}
          </div>

          {/* Desktop: table */}
          <div className="hidden md:block overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Tgl & Jam</TableHead>
                <TableHead>Pasien</TableHead>
                <TableHead>Treatment</TableHead>
                <TableHead>Dokter / Therapist</TableHead>
                <TableHead>Tagging CS</TableHead>
                <TableHead>Kategori Pasien</TableHead>
                <TableHead>Sumber</TableHead>
                <TableHead>Room</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.slice(0, 200).map((b) => (
                <TableRow key={b.id} data-testid={`booking-row-${b.id}`}>
                  <TableCell className="text-sm">{fmtDateTime(b.booking_at)}</TableCell>
                  <TableCell>
                    <div className="font-medium text-sm">{b.patient_name}</div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Phone className="h-3 w-3" /> {b.patient_phone}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm">{treatmentLabel(b)}</TableCell>
                  <TableCell className="text-sm">{b.doctor_name || "—"}</TableCell>
                  <TableCell className="text-xs">{b.cs_tag}</TableCell>
                  <TableCell className="text-xs" data-testid={`booking-category-${b.id}`}>
                    {b.customer_category ? (
                      <Badge variant="outline" className={b.customer_category === "new_customer" ? "border-sky-300 text-sky-700" : "border-slate-300 text-slate-700"}>
                        {CAT_LABEL[b.customer_category] || b.customer_category}
                      </Badge>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-xs" data-testid={`booking-source-col-${b.id}`}>
                    {b.booking_source ? (SOURCE_LABEL[b.booking_source] || b.booking_source) : <span className="text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell className="text-xs">{b.request_room || "—"}</TableCell>
                  <TableCell>
                    <StatusBadge status={b.status} />
                    {b.attendance_status && b.attendance_status !== "pending" && (
                      <span className="mt-1 block text-[10px] font-medium text-emerald-700" data-testid={`att-badge-${b.id}`}>{ATT_LABEL[b.attendance_status]}</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1.5">
                      {canEdit && (
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid={`edit-booking-${b.id}`}
                          onClick={() => openEdit(b)}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      {b.status !== "finished" && b.status !== "cancel" && (
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid={`tx-booking-${b.id}`}
                          onClick={() => openTx(b)}
                          title="Catat transaksi & finish"
                        >
                          <Receipt className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      <Select onValueChange={(v) => updateStatus(b.id, v)} value={undefined}>
                        <SelectTrigger
                          data-testid={`status-select-${b.id}`}
                          className="w-[110px]"
                        >
                          <SelectValue placeholder="Status" />
                        </SelectTrigger>
                        <SelectContent>
                          {STATUSES.map((s) => (
                            <SelectItem key={s} value={s} className="capitalize">
                              {s}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {canAttendance && (
                        <Select onValueChange={(v) => updateAttendance(b.id, v)} value={undefined}>
                          <SelectTrigger data-testid={`attendance-select-${b.id}`} className="w-[130px]">
                            <SelectValue placeholder="Kehadiran" />
                          </SelectTrigger>
                          <SelectContent>
                            {ATTENDANCE_OPTS.map(([v, l]) => (
                              <SelectItem key={v} value={v}>{l}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                      {canDelete && (
                        <Button
                          size="sm"
                          variant="ghost"
                          data-testid={`delete-booking-${b.id}`}
                          onClick={() => deleteBooking(b.id)}
                          className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={10} className="text-center text-sm text-muted-foreground py-10">
                    Tidak ada booking.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          </div>
        </CardContent>
      </Card>

      <BookingFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        booking={editing}
        branches={branches}
        onSaved={fetchBookings}
      />
      <TransactionFormDialog
        open={txOpen}
        onOpenChange={setTxOpen}
        booking={txBooking}
        onSaved={fetchBookings}
      />
    </div>
  );
}
